import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  BookOpen, Users, Award, Clock, AlertTriangle, Plus, Edit, Trash2, 
  Send, RefreshCw, CheckCircle2, XCircle, BarChart3, FileText, Upload, Video 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrainingManagement({ token }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [trainings, setTrainings] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [editingAssessment, setEditingAssessment] = useState(null);

  // Form states
  const [trainingForm, setTrainingForm] = useState({
    title: '',
    description: '',
    category: '',
    video_url: '',
    video_file_id: '',
    text_content: '',
    duration_minutes: 30
  });
  
  const [assignForm, setAssignForm] = useState({
    training_id: '',
    employee_ids: [],
    deadline: ''
  });

  const [assessmentForm, setAssessmentForm] = useState({
    training_id: '',
    title: '',
    passing_score: 80,
    time_limit_minutes: 30,
    questions: []
  });

  // Video upload states
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoSource, setVideoSource] = useState('url'); // 'url' or 'upload'
  const videoInputRef = useRef(null);

  const fetchTrainings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/trainings?include_inactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTrainings(data.trainings || []);
    } catch (error) {
      console.error('Error fetching trainings:', error);
    }
  }, [token]);

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAssignments(data.assignments || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  }, [token]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEmployees(data.filter(u => u.role !== 'admin' && u.role !== 'superadmin' && u.is_active !== false));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, [token]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTrainings(), fetchAssignments(), fetchEmployees(), fetchDashboard()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTrainings, fetchAssignments, fetchEmployees, fetchDashboard]);

  const seedSampleTrainings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/trainings/seed-samples`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      toast.success(data.message);
      fetchTrainings();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to seed trainings');
    }
  };

  const triggerReminders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/trigger-reminders`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Reminders triggered successfully');
      }
    } catch (error) {
      toast.error('Failed to trigger reminders');
    }
  };

  const openTrainingDialog = (training = null) => {
    if (training) {
      setEditingTraining(training);
      setTrainingForm({
        title: training.title,
        description: training.description,
        category: training.category,
        video_url: training.video_url || '',
        video_file_id: training.video_file_id || '',
        text_content: training.text_content || '',
        duration_minutes: training.duration_minutes
      });
      setVideoSource(training.video_file_id ? 'upload' : 'url');
    } else {
      setEditingTraining(null);
      setTrainingForm({
        title: '',
        description: '',
        category: '',
        video_url: '',
        video_file_id: '',
        text_content: '',
        duration_minutes: 30
      });
      setVideoSource('url');
    }
    setVideoFile(null);
    setUploadProgress(0);
    setShowTrainingDialog(true);
  };

  const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB per chunk to stay under proxy limits

  const uploadVideo = async () => {
    if (!videoFile) return null;
    setUploadingVideo(true);
    setUploadProgress(0);
    
    const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2);
    const totalChunks = Math.ceil(videoFile.size / CHUNK_SIZE);
    
    try {
      // Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, videoFile.size);
        const chunk = videoFile.slice(start, end);
        
        const formData = new FormData();
        formData.append('file', chunk, `chunk_${i}`);
        formData.append('chunk_index', i.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('upload_id', uploadId);
        formData.append('filename', videoFile.name);
        
        const res = await fetch(`${API_URL}/api/trainings/upload-chunk`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Chunk ${i + 1} upload failed`);
        }
        
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 80));
      }
      
      // Finalize upload - assemble chunks and upload to storage
      setUploadProgress(85);
      const finalFormData = new FormData();
      finalFormData.append('upload_id', uploadId);
      finalFormData.append('filename', videoFile.name);
      finalFormData.append('total_chunks', totalChunks.toString());
      finalFormData.append('content_type', videoFile.type || 'video/mp4');
      
      const finalRes = await fetch(`${API_URL}/api/trainings/finalize-upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: finalFormData
      });
      
      if (!finalRes.ok) {
        const err = await finalRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Finalize upload failed');
      }
      
      const data = await finalRes.json();
      setUploadProgress(100);
      toast.success('Video uploaded successfully');
      return data.file_id;
    } catch (error) {
      console.error('Video upload failed:', error);
      toast.error('Video upload failed: ' + error.message);
      return null;
    } finally {
      setUploadingVideo(false);
    }
  };

  const saveTraining = async () => {
    try {
      let formToSend = { ...trainingForm };
      
      // Upload video if a new file was selected
      if (videoSource === 'upload' && videoFile) {
        const fileId = await uploadVideo();
        if (!fileId) return;
        formToSend.video_file_id = fileId;
        formToSend.video_url = ''; // Clear URL when uploading file
      } else if (videoSource === 'url') {
        formToSend.video_file_id = ''; // Clear file when using URL
      }
      
      const url = editingTraining 
        ? `${API_URL}/api/trainings/${editingTraining.id}`
        : `${API_URL}/api/trainings`;
      
      const res = await fetch(url, {
        method: editingTraining ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formToSend)
      });

      if (res.ok) {
        toast.success(editingTraining ? 'Training updated' : 'Training created');
        setShowTrainingDialog(false);
        fetchTrainings();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save training');
      }
    } catch (error) {
      toast.error('Failed to save training');
    }
  };

  const deleteTraining = async (trainingId) => {
    if (!window.confirm('Are you sure you want to delete this training?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/trainings/${trainingId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Training deleted');
        fetchTrainings();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to delete training');
    }
  };

  const openAssignDialog = (training) => {
    setAssignForm({
      training_id: training.id,
      employee_ids: [],
      deadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 14 days from now
    });
    setShowAssignDialog(true);
  };

  const assignTraining = async () => {
    if (assignForm.employee_ids.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/training-assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(assignForm)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowAssignDialog(false);
        fetchAssignments();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to assign training');
      }
    } catch (error) {
      toast.error('Failed to assign training');
    }
  };

  const toggleEmployeeSelection = (empId) => {
    setAssignForm(prev => ({
      ...prev,
      employee_ids: prev.employee_ids.includes(empId)
        ? prev.employee_ids.filter(id => id !== empId)
        : [...prev.employee_ids, empId]
    }));
  };

  const selectAllEmployees = () => {
    setAssignForm(prev => ({
      ...prev,
      employee_ids: prev.employee_ids.length === employees.length 
        ? [] 
        : employees.map(e => e.employee_id)
    }));
  };

  // --- Assessment (Q&A) Functions ---
  const openAssessmentDialog = async (training) => {
    setAssessmentForm({
      training_id: training.id,
      title: `${training.title} - Assessment`,
      passing_score: 80,
      time_limit_minutes: 30,
      questions: []
    });
    setEditingAssessment(null);

    // Check if assessment already exists for this training
    try {
      const res = await fetch(`${API_URL}/api/assessments/by-training/${training.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.assessment) {
          setEditingAssessment(data.assessment);
          setAssessmentForm({
            training_id: training.id,
            title: data.assessment.title,
            passing_score: data.assessment.passing_score,
            time_limit_minutes: data.assessment.time_limit_minutes,
            questions: (data.assessment.questions || []).map(q => ({
              ...q,
              correct_answers: q.correct_answers || (q.correct_answer !== null && q.correct_answer !== undefined ? [q.correct_answer] : [])
            }))
          });
        }
      }
    } catch (error) {
      console.error('Error loading assessment:', error);
    }
    setShowAssessmentDialog(true);
  };

  const addQuestion = () => {
    setAssessmentForm(prev => ({
      ...prev,
      questions: [...prev.questions, {
        id: Date.now().toString(),
        question: '',
        options: ['', ''],
        correct_answers: [],
        points: 10
      }]
    }));
  };

  const updateQuestion = (qIndex, field, value) => {
    setAssessmentForm(prev => {
      const questions = [...prev.questions];
      questions[qIndex] = { ...questions[qIndex], [field]: value };
      return { ...prev, questions };
    });
  };

  const addOption = (qIndex) => {
    setAssessmentForm(prev => {
      const questions = [...prev.questions];
      questions[qIndex] = { ...questions[qIndex], options: [...questions[qIndex].options, ''] };
      return { ...prev, questions };
    });
  };

  const updateOption = (qIndex, oIndex, value) => {
    setAssessmentForm(prev => {
      const questions = [...prev.questions];
      const options = [...questions[qIndex].options];
      options[oIndex] = value;
      questions[qIndex] = { ...questions[qIndex], options };
      return { ...prev, questions };
    });
  };

  const removeOption = (qIndex, oIndex) => {
    setAssessmentForm(prev => {
      const questions = [...prev.questions];
      const options = questions[qIndex].options.filter((_, i) => i !== oIndex);
      // Adjust correct_answers indices
      const correct = questions[qIndex].correct_answers
        .filter(i => i !== oIndex)
        .map(i => i > oIndex ? i - 1 : i);
      questions[qIndex] = { ...questions[qIndex], options, correct_answers: correct };
      return { ...prev, questions };
    });
  };

  const toggleCorrectAnswer = (qIndex, oIndex) => {
    setAssessmentForm(prev => {
      const questions = [...prev.questions];
      const correct = [...questions[qIndex].correct_answers];
      const idx = correct.indexOf(oIndex);
      if (idx > -1) {
        correct.splice(idx, 1);
      } else {
        correct.push(oIndex);
      }
      questions[qIndex] = { ...questions[qIndex], correct_answers: correct };
      return { ...prev, questions };
    });
  };

  const removeQuestion = (qIndex) => {
    setAssessmentForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== qIndex)
    }));
  };

  const saveAssessment = async () => {
    // Validate
    if (assessmentForm.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }
    for (let i = 0; i < assessmentForm.questions.length; i++) {
      const q = assessmentForm.questions[i];
      if (!q.question.trim()) {
        toast.error(`Question ${i + 1} text is empty`);
        return;
      }
      if (q.options.some(o => !o.trim())) {
        toast.error(`Question ${i + 1} has empty options`);
        return;
      }
      if (q.correct_answers.length === 0) {
        toast.error(`Question ${i + 1} has no correct answer selected`);
        return;
      }
    }

    try {
      const url = editingAssessment
        ? `${API_URL}/api/assessments/${editingAssessment.id}`
        : `${API_URL}/api/assessments`;
      
      const res = await fetch(url, {
        method: editingAssessment ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(assessmentForm)
      });

      if (res.ok) {
        toast.success(editingAssessment ? 'Assessment updated' : 'Assessment created');
        setShowAssessmentDialog(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save assessment');
      }
    } catch (error) {
      toast.error('Failed to save assessment');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status, deadline) => {
    const isOverdue = new Date(deadline) < new Date() && !['completed'].includes(status);
    
    if (status === 'completed') return <Badge className="bg-green-500">Completed</Badge>;
    if (status === 'failed') return <Badge variant="destructive">Failed</Badge>;
    if (isOverdue) return <Badge variant="destructive">Overdue</Badge>;
    if (status === 'in_progress') return <Badge className="bg-blue-500">In Progress</Badge>;
    return <Badge variant="secondary">Pending</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="admin-training-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-training-management">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4" data-testid="admin-training-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4 mr-2" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="trainings" data-testid="tab-trainings">
            <BookOpen className="h-4 w-4 mr-2" /> Trainings
          </TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">
            <Users className="h-4 w-4 mr-2" /> Assignments
          </TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tab-overdue">
            <AlertTriangle className="h-4 w-4 mr-2" /> Overdue
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          {dashboard && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card data-testid="dashboard-total-trainings">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Trainings</p>
                        <p className="text-2xl font-bold">{dashboard.summary.total_trainings}</p>
                      </div>
                      <BookOpen className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card data-testid="dashboard-total-assignments">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Assignments</p>
                        <p className="text-2xl font-bold">{dashboard.summary.total_assignments}</p>
                      </div>
                      <Users className="h-8 w-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card data-testid="dashboard-completed">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Completed</p>
                        <p className="text-2xl font-bold text-green-600">{dashboard.summary.completed}</p>
                      </div>
                      <CheckCircle2 className="h-8 w-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card data-testid="dashboard-overdue">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Overdue</p>
                        <p className="text-2xl font-bold text-red-600">{dashboard.summary.overdue}</p>
                      </div>
                      <AlertTriangle className="h-8 w-8 text-red-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Training Completion Rates</CardTitle>
                  <CardDescription>Overview of all training programs</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {dashboard.training_stats.map((stat) => (
                      <div key={stat.training_id} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{stat.title}</span>
                            <Badge variant="outline" className="ml-2">{stat.category}</Badge>
                          </div>
                          <span className="text-sm text-gray-500">
                            {stat.completed}/{stat.assigned} ({stat.completion_rate}%)
                          </span>
                        </div>
                        <Progress value={stat.completion_rate} className="h-2" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button onClick={seedSampleTrainings} variant="outline" data-testid="btn-seed-trainings">
                  <Plus className="h-4 w-4 mr-2" /> Seed Sample Trainings
                </Button>
                <Button onClick={triggerReminders} variant="outline" data-testid="btn-trigger-reminders">
                  <RefreshCw className="h-4 w-4 mr-2" /> Trigger Reminders
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* Trainings Tab */}
        <TabsContent value="trainings" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openTrainingDialog()} data-testid="btn-add-training">
              <Plus className="h-4 w-4 mr-2" /> Add Training
            </Button>
          </div>

          <Table data-testid="trainings-table">
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Video</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trainings.map((training) => (
                <TableRow key={training.id}>
                  <TableCell className="font-medium">{training.title}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{training.category}</Badge>
                  </TableCell>
                  <TableCell>
                    {training.video_file_id ? (
                      <Badge className="bg-blue-500"><Video className="h-3 w-3 mr-1 inline" />Uploaded</Badge>
                    ) : training.video_url ? (
                      <Badge variant="outline"><Video className="h-3 w-3 mr-1 inline" />URL</Badge>
                    ) : (
                      <span className="text-gray-400 text-sm">None</span>
                    )}
                  </TableCell>
                  <TableCell>{training.duration_minutes} min</TableCell>
                  <TableCell>
                    {training.is_active ? (
                      <Badge className="bg-green-500">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openAssignDialog(training)} data-testid={`btn-assign-${training.id}`}>
                        <Send className="h-4 w-4 mr-1" /> Assign
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openAssessmentDialog(training)} data-testid={`btn-assessment-${training.id}`}>
                        <FileText className="h-4 w-4 mr-1" /> Q&A
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openTrainingDialog(training)} data-testid={`btn-edit-${training.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteTraining(training.id)} data-testid={`btn-delete-${training.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="space-y-4">
          <Table data-testid="assignments-table">
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Training</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{assignment.employee_name}</p>
                      <p className="text-sm text-gray-500">{assignment.employee_id}</p>
                    </div>
                  </TableCell>
                  <TableCell>{assignment.training_title}</TableCell>
                  <TableCell>{formatDate(assignment.assigned_at)}</TableCell>
                  <TableCell>{formatDate(assignment.deadline)}</TableCell>
                  <TableCell>{getStatusBadge(assignment.status, assignment.deadline)}</TableCell>
                  <TableCell>
                    {assignment.score !== null ? `${assignment.score.toFixed(1)}%` : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        {/* Overdue Tab */}
        <TabsContent value="overdue" className="space-y-4">
          {assignments.filter(a => 
            new Date(a.deadline) < new Date() && !['completed'].includes(a.status)
          ).length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p>No overdue trainings. Great job!</p>
              </CardContent>
            </Card>
          ) : (
            <Table data-testid="overdue-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Training</TableHead>
                  <TableHead>Deadline</TableHead>
                  <TableHead>Days Overdue</TableHead>
                  <TableHead>Reminders Sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments
                  .filter(a => new Date(a.deadline) < new Date() && !['completed'].includes(a.status))
                  .map((assignment) => {
                    const daysOverdue = Math.floor((Date.now() - new Date(assignment.deadline)) / (1000 * 60 * 60 * 24));
                    const remindersSent = [assignment.reminder_1_sent, assignment.reminder_2_sent, assignment.reminder_3_sent].filter(Boolean).length;
                    
                    return (
                      <TableRow key={assignment.id} className="bg-red-50">
                        <TableCell>
                          <div>
                            <p className="font-medium">{assignment.employee_name}</p>
                            <p className="text-sm text-gray-500">{assignment.employee_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{assignment.training_title}</TableCell>
                        <TableCell className="text-red-600">{formatDate(assignment.deadline)}</TableCell>
                        <TableCell>
                          <Badge variant="destructive">{daysOverdue} days</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {[1, 2, 3].map(n => (
                              <div
                                key={n}
                                className={`h-2 w-2 rounded-full ${
                                  assignment[`reminder_${n}_sent`] ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                                title={`Reminder ${n}`}
                              />
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>

      {/* Training Dialog */}
      <Dialog open={showTrainingDialog} onOpenChange={setShowTrainingDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="training-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingTraining ? 'Edit Training' : 'Create Training'}</DialogTitle>
            <DialogDescription>
              {editingTraining ? 'Update the training details' : 'Add a new training program'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={trainingForm.title}
                  onChange={(e) => setTrainingForm({ ...trainingForm, title: e.target.value })}
                  placeholder="e.g., Information Security Awareness"
                  data-testid="input-title"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={trainingForm.category || undefined}
                  onValueChange={(value) => setTrainingForm({ ...trainingForm, category: value })}
                >
                  <SelectTrigger data-testid="select-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="InfoSec">Information Security</SelectItem>
                    <SelectItem value="COBEC">Code of Business Ethics</SelectItem>
                    <SelectItem value="ABEC">Anti-Bribery & Corruption</SelectItem>
                    <SelectItem value="Data Security">Data Security & Privacy</SelectItem>
                    <SelectItem value="Compliance">Compliance</SelectItem>
                    <SelectItem value="HR">HR Policies</SelectItem>
                    <SelectItem value="Technical">Technical Skills</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={trainingForm.description}
                onChange={(e) => setTrainingForm({ ...trainingForm, description: e.target.value })}
                placeholder="Brief description of the training..."
                rows={3}
                data-testid="input-description"
              />
            </div>

            {/* Video Source Selection */}
            <div className="grid gap-3">
              <Label>Video Content</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={videoSource === 'url' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setVideoSource('url')}
                  data-testid="btn-video-url"
                >
                  <Video className="h-4 w-4 mr-1" /> Video URL
                </Button>
                <Button
                  type="button"
                  variant={videoSource === 'upload' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setVideoSource('upload')}
                  data-testid="btn-video-upload"
                >
                  <Upload className="h-4 w-4 mr-1" /> Upload Video
                </Button>
              </div>

              {videoSource === 'url' && (
                <Input
                  value={trainingForm.video_url}
                  onChange={(e) => setTrainingForm({ ...trainingForm, video_url: e.target.value })}
                  placeholder="https://www.youtube.com/embed/..."
                  data-testid="input-video-url"
                />
              )}

              {videoSource === 'upload' && (
                <div className="space-y-2">
                  {trainingForm.video_file_id && !videoFile && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-700">Video already uploaded</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => videoInputRef.current?.click()}
                      >
                        Replace
                      </Button>
                    </div>
                  )}
                  <div
                    className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                    onClick={() => videoInputRef.current?.click()}
                    data-testid="video-upload-area"
                  >
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setVideoFile(file);
                      }}
                    />
                    {videoFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <Video className="h-5 w-5 text-blue-600" />
                        <span className="font-medium text-blue-700">{videoFile.name}</span>
                        <span className="text-sm text-gray-500">
                          ({(videoFile.size / (1024 * 1024)).toFixed(1)} MB)
                        </span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">Click to upload video (any size)</p>
                        <p className="text-xs text-gray-400 mt-1">MP4, WebM, AVI, MOV supported</p>
                      </div>
                    )}
                  </div>
                  {uploadingVideo && (
                    <div className="space-y-1">
                      <Progress value={uploadProgress} className="h-2" />
                      <p className="text-xs text-gray-500 text-center">Uploading... {uploadProgress}%</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="text_content">Training Content / Transcript</Label>
              <Textarea
                id="text_content"
                value={trainingForm.text_content}
                onChange={(e) => setTrainingForm({ ...trainingForm, text_content: e.target.value })}
                placeholder="Key points and content of the training..."
                rows={3}
                data-testid="input-text-content"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                value={trainingForm.duration_minutes}
                onChange={(e) => setTrainingForm({ ...trainingForm, duration_minutes: parseInt(e.target.value) || 30 })}
                min={5}
                max={180}
                data-testid="input-duration"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrainingDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveTraining} disabled={uploadingVideo} data-testid="btn-save-training">
              {uploadingVideo ? 'Uploading...' : editingTraining ? 'Update' : 'Create'} Training
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign Training</DialogTitle>
            <DialogDescription>
              Select employees to assign this training to
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Deadline *</Label>
              <Input
                type="date"
                value={assignForm.deadline}
                onChange={(e) => setAssignForm({ ...assignForm, deadline: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                data-testid="input-deadline"
              />
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Select Employees ({assignForm.employee_ids.length} selected)</Label>
                <Button variant="link" size="sm" onClick={selectAllEmployees}>
                  {assignForm.employee_ids.length === employees.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {employees.map((emp) => (
                  <div
                    key={emp.employee_id}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <Checkbox
                      id={emp.employee_id}
                      checked={assignForm.employee_ids.includes(emp.employee_id)}
                      onCheckedChange={() => toggleEmployeeSelection(emp.employee_id)}
                    />
                    <Label htmlFor={emp.employee_id} className="flex-1 cursor-pointer">
                      <span className="font-medium">{emp.name}</span>
                      <span className="text-sm text-gray-500 ml-2">({emp.employee_id})</span>
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={assignTraining} disabled={assignForm.employee_ids.length === 0} data-testid="btn-confirm-assign">
              Assign to {assignForm.employee_ids.length} Employee(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assessment Q&A Builder Dialog */}
      <Dialog open={showAssessmentDialog} onOpenChange={setShowAssessmentDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="assessment-dialog">
          <DialogHeader>
            <DialogTitle>{editingAssessment ? 'Edit Assessment' : 'Create Assessment'}</DialogTitle>
            <DialogDescription>
              Build questions with checkboxes. Check correct answer(s) for each question.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label>Assessment Title</Label>
                <Input
                  value={assessmentForm.title}
                  onChange={(e) => setAssessmentForm({ ...assessmentForm, title: e.target.value })}
                  placeholder="Assessment title"
                  data-testid="input-assessment-title"
                />
              </div>
              <div className="grid gap-2">
                <Label>Passing Score (%)</Label>
                <Input
                  type="number"
                  value={assessmentForm.passing_score}
                  onChange={(e) => setAssessmentForm({ ...assessmentForm, passing_score: parseInt(e.target.value) || 80 })}
                  min={1}
                  max={100}
                  data-testid="input-passing-score"
                />
              </div>
              <div className="grid gap-2">
                <Label>Time Limit (min)</Label>
                <Input
                  type="number"
                  value={assessmentForm.time_limit_minutes}
                  onChange={(e) => setAssessmentForm({ ...assessmentForm, time_limit_minutes: parseInt(e.target.value) || 30 })}
                  min={5}
                  max={120}
                  data-testid="input-time-limit"
                />
              </div>
            </div>

            {/* Questions Builder */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Questions ({assessmentForm.questions.length})</Label>
                <Button type="button" size="sm" onClick={addQuestion} data-testid="btn-add-question">
                  <Plus className="h-4 w-4 mr-1" /> Add Question
                </Button>
              </div>

              {assessmentForm.questions.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <FileText className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500 text-sm">No questions yet. Click "Add Question" to start.</p>
                </div>
              )}

              {assessmentForm.questions.map((q, qIndex) => (
                <Card key={q.id} className="border-l-4 border-l-blue-500" data-testid={`question-card-${qIndex}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-sm text-gray-500">Question {qIndex + 1}</CardTitle>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Label className="text-xs text-gray-500">Points:</Label>
                          <Input
                            type="number"
                            value={q.points}
                            onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value) || 10)}
                            className="w-16 h-7 text-xs"
                            min={1}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500 h-7"
                          onClick={() => removeQuestion(qIndex)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input
                      value={q.question}
                      onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                      placeholder="Type your question here..."
                      data-testid={`input-question-${qIndex}`}
                    />
                    
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-500">Options (check correct answers):</Label>
                      {q.options.map((option, oIndex) => (
                        <div key={oIndex} className="flex items-center gap-2">
                          <Checkbox
                            checked={q.correct_answers.includes(oIndex)}
                            onCheckedChange={() => toggleCorrectAnswer(qIndex, oIndex)}
                            data-testid={`checkbox-correct-${qIndex}-${oIndex}`}
                          />
                          <Input
                            value={option}
                            onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                            placeholder={`Option ${oIndex + 1}`}
                            className="flex-1"
                            data-testid={`input-option-${qIndex}-${oIndex}`}
                          />
                          {q.options.length > 2 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-gray-400 hover:text-red-500 h-8 w-8 p-0"
                              onClick={() => removeOption(qIndex, oIndex)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-blue-600"
                        onClick={() => addOption(qIndex)}
                      >
                        <Plus className="h-3 w-3 mr-1" /> Add Option
                      </Button>
                    </div>
                    {q.correct_answers.length > 0 && (
                      <p className="text-xs text-green-600">
                        {q.correct_answers.length} correct answer(s) selected
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssessmentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveAssessment} data-testid="btn-save-assessment">
              {editingAssessment ? 'Update' : 'Create'} Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
